import axios from "axios";

import { ThunkDispatch } from "redux-thunk";
import { Action } from "redux";
import { loadAndTrack } from "redux-request-loading";

import { Payload } from "@neworbit/redux-helpers";

import { FileAnswer } from "@teamprevent/common/questionnaires/model";
import { toast } from "@teamprevent/common/toasts";
import { ImmunisationListModel } from "@teamprevent/common/immunisation/models";

import { ImmunisationsApi } from "./ImmunisationsApi";
import {
    CREATE_IMMUNISATION,
    CREATE_IMMUNISATION_SUCCESS,
    LOAD_IMMUNISATIONS_SUCCESS,
    LOAD_IMMUNISATIONS,
    SAVE_IMMUNISATION_SUCCESS,
    SAVE_IMMUNISATION,
    DELETE_IMMUNISATION_SUCCESS,
    DELETE_IMMUNISATION,
    LOAD_IMMUNISATION_SUCCESS,
    LOAD_IMMUNISATION,
    CREATE_IMMUNISATIONS,
    SEND_IMMUNISATIONS_HISTORY
} from "./actionTypes";
import { ImmunisationAndEvidenceCreateModel, ImmunisationCreateModel, ImmunisationEditModel, ImmunisationDetailModel } from "./model";

import { push } from "redux-little-router";

export type LoadImmunisationsSuccess = ({ type: typeof LOAD_IMMUNISATIONS_SUCCESS }) & Payload<ImmunisationListModel[]>;
export type LoadImmunisationSuccess = ({ type: typeof LOAD_IMMUNISATION_SUCCESS }) & Payload<ImmunisationDetailModel>;
export type CreateImmunisationSuccess = ({ type: typeof CREATE_IMMUNISATION_SUCCESS }) & Payload<ImmunisationListModel>;
export type SaveImmunisationSuccess = ({ type: typeof SAVE_IMMUNISATION_SUCCESS }) & Payload<ImmunisationListModel>;
export type DeleteImmunisationSuccess = ({ type: typeof DELETE_IMMUNISATION_SUCCESS }) & Payload<string>;

export type ImmunisationAction =
    LoadImmunisationsSuccess |
    LoadImmunisationSuccess |
    CreateImmunisationSuccess |
    SaveImmunisationSuccess |
    DeleteImmunisationSuccess;

export const buildCreateImmunisation = (immunisationsApi: ImmunisationsApi) => {
    return (employeeRecordId: string, customerId: string, immunisation: ImmunisationAndEvidenceCreateModel, basePath: string) => {
        return async (dispatch: ThunkDispatch<any, undefined, Action>) => {
            const response = await loadAndTrack(dispatch, CREATE_IMMUNISATION, immunisationsApi.createImmunisation(
                employeeRecordId,
                customerId, {
                    ...immunisation,
                    dateCompleted: immunisation.dateCompleted.format("YYYY-MM-DD")
                }));
            dispatch(createImmunisationSuccess(response));
            dispatch(push(`${basePath}/${response.id}`));
        };
    };
};

export const createImmunisation = buildCreateImmunisation(new ImmunisationsApi(axios));

export const buildCreateImmunisations = (immunisationsApi: ImmunisationsApi) => {
    return (employeeRecordId: string, customerId: string, immunisations: ImmunisationCreateModel[], evidence: FileAnswer) => {
        return async (dispatch: ThunkDispatch<any, undefined, Action>) => {
            const immunisationModels = immunisations.map(i => ({ ...i, dateCompleted: i.dateCompleted.format("YYYY-MM-DD") }));
            await loadAndTrack(
                dispatch, CREATE_IMMUNISATIONS, immunisationsApi.createImmunisations(employeeRecordId, customerId, immunisationModels, evidence));
        };
    };
};

export const createImmunisations = buildCreateImmunisations(new ImmunisationsApi(axios));

export const createImmunisationSuccess = (immunisation: ImmunisationListModel): CreateImmunisationSuccess => {
    return {
        type: CREATE_IMMUNISATION_SUCCESS,
        payload: immunisation
    };
};

export const buildLoadEmployeeImmunisations = (immunisationsApi: ImmunisationsApi) => {
    return ({ employeeRecordId }: { employeeRecordId: string }) => {
        return async (dispatch: ThunkDispatch<any, undefined, Action>) => {
            const response = await loadAndTrack(dispatch, LOAD_IMMUNISATIONS, immunisationsApi.getAll(employeeRecordId));
            dispatch(loadImmunisationsSuccess(response));
        };
    };
};

export const loadEmployeeImmunisations = buildLoadEmployeeImmunisations(new ImmunisationsApi(axios));

export const loadImmunisationsSuccess = (immunisations: ImmunisationListModel[]): LoadImmunisationsSuccess => {
    return {
        type: LOAD_IMMUNISATIONS_SUCCESS,
        payload: immunisations
    };
};

export const buildLoadImmunisationDetail = (immunisationsApi: ImmunisationsApi) => {
    return ({ employeeRecordId, immunisationId }: { employeeRecordId: string; immunisationId: string }) => {
        return async (dispatch: ThunkDispatch<any, undefined, Action>) => {
            const response = await loadAndTrack(dispatch, LOAD_IMMUNISATION, immunisationsApi.get(employeeRecordId, immunisationId));
            dispatch(loadImmunisationSuccess(response));
        };
    };
};

export const loadImmunisationDetail = buildLoadImmunisationDetail(new ImmunisationsApi(axios));

export const loadImmunisationSuccess = (immunisation: ImmunisationDetailModel): LoadImmunisationSuccess => {
    return {
        type: LOAD_IMMUNISATION_SUCCESS,
        payload: immunisation
    };
};

export const buildSaveImmunisation = (immunisationsApi: ImmunisationsApi) => {
    return (employeeRecordId: string, immunisation: ImmunisationEditModel, basePath: string) => {
        return async (dispatch: ThunkDispatch<any, undefined, Action>) => {
            const response = await loadAndTrack(dispatch, SAVE_IMMUNISATION, immunisationsApi.saveImmunisation(
                employeeRecordId,
                {
                    ...immunisation,
                    dateCompleted: immunisation.dateCompleted.format("YYYY-MM-DD")
                }));
            dispatch(saveImmunisationSuccess(response));
            dispatch(push(`${basePath}/${response.id}`));
        };
    };
};

export const saveImmunisation = buildSaveImmunisation(new ImmunisationsApi(axios));

export const buildSendImmsHistory = (immunisationsApi: ImmunisationsApi) => {
    return ({ customerId, employeeRecordId }: { customerId: string; employeeRecordId: string }) => {
        return async (dispatch: ThunkDispatch<any, undefined, Action>) => {
            await loadAndTrack(dispatch, SEND_IMMUNISATIONS_HISTORY, immunisationsApi.sendImmsHistory(customerId, employeeRecordId));
            toast.success("Imms History Email sent to employee");
        };
    };
};

export const sendImmsHistory = buildSendImmsHistory(new ImmunisationsApi(axios));

export const saveImmunisationSuccess = (immunisation: ImmunisationListModel): SaveImmunisationSuccess => {
    return {
        type: SAVE_IMMUNISATION_SUCCESS,
        payload: immunisation
    };
};

export const buildDeleteImmunisation = (immunisationsApi: ImmunisationsApi) => {
    return (employeeRecordId: string, id: string, basePath: string) => {
        return async (dispatch: ThunkDispatch<any, undefined, Action>) => {
            await loadAndTrack(dispatch, DELETE_IMMUNISATION, immunisationsApi.deleteImmunisation(employeeRecordId, id));
            dispatch(deleteImmunisationSuccess(id));
            dispatch(push(basePath));
        };
    };
};

export const deleteImmunisation = buildDeleteImmunisation(new ImmunisationsApi(axios));

export const deleteImmunisationSuccess = (id: string): DeleteImmunisationSuccess => {
    return {
        type: DELETE_IMMUNISATION_SUCCESS,
        payload: id
    };
};
